import { Link } from "react-router-dom";
import { Trash2, Plus, Minus, ShoppingBag } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";

export default function CartPage() {
  const { t } = useLanguage();
  const { items, removeItem, updateQty, total } = useCart();

  return (
    <div style={{ background: "#050505", minHeight: "100vh" }}>
      <SEO
        title={t.cart.seoTitle}
        description={t.cart.seoDesc}
        url="/kosik"
        noindex
      />
      <Navbar />
      <div style={{ paddingTop: "5rem" }}>
        <section style={{ padding: "4rem 1.5rem 2rem", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="max-w-5xl mx-auto">
            <p className="section-label mb-3">REBELTEAM</p>
            <h1 style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 900, fontSize: "clamp(2rem, 6vw, 4rem)", color: "#FFFFFF", textTransform: "uppercase", letterSpacing: "-0.03em" }}>
              {t.cart.title}
            </h1>
          </div>
        </section>

        <div className="max-w-5xl mx-auto px-6 py-8">
          {items.length === 0 ? (
            <div data-testid="empty-cart" className="text-center py-20">
              <ShoppingBag size={64} style={{ color: "#52525B", margin: "0 auto 1.5rem" }} />
              <p style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, fontSize: "1rem", color: "#FFFFFF", textTransform: "uppercase", marginBottom: "0.5rem" }}>
                {t.cart.empty}
              </p>
              <p style={{ color: "#52525B", marginBottom: "2rem" }}>{t.cart.emptySub}</p>
              <Link to="/merch" data-testid="continue-shopping" className="btn-rebel rounded-none inline-block">
                {t.cart.continueShopping}
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Items — one row per product */}
              <div className="lg:col-span-2 flex flex-col gap-3">
                {items.map((item, i) => (
                  <div
                    key={`${item._id}-${item.size}-${item.color}`}
                    data-testid={`cart-item-${i}`}
                    className="glass-card rounded-xl flex items-center gap-3"
                    style={{ padding: "0.75rem 1rem" }}
                  >
                    <div style={{ width: "52px", height: "52px", borderRadius: "6px", overflow: "hidden", background: "#121212", flexShrink: 0 }}>
                      <img
                        src={item.image_url}
                        alt={item.name}
                        loading="lazy"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={e => { e.target.style.display = "none"; }}
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p
                        style={{
                          fontFamily: "Unbounded, sans-serif",
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          color: "#FFFFFF",
                          textTransform: "uppercase",
                          lineHeight: 1.25,
                          margin: 0,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={item.name}
                      >
                        {item.name}
                      </p>
                      <p style={{ color: "#A1A1AA", fontSize: "0.68rem", margin: "0.2rem 0 0", fontFamily: "Manrope, sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        <span style={{ color: "#FF005A", fontFamily: "Unbounded, sans-serif", fontWeight: 700 }}>{item.price} Kč</span>
                        <span style={{ margin: "0 0.25rem" }}>×</span>
                        <span>{item.quantity} {t.cart.unit}</span>
                        {item.size && <span style={{ marginLeft: "0.45rem" }}>{t.merch.size}: {item.size}</span>}
                        {item.color && <span style={{ marginLeft: "0.45rem" }}>{item.color}</span>}
                        {item.custom_options && Object.entries(item.custom_options).map(([k, v]) => (
                          <span key={k} style={{ marginLeft: "0.45rem" }}>{k}: {v}</span>
                        ))}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        data-testid={`decrease-${i}`}
                        onClick={() => updateQty(item._id, item.size, item.color, item.quantity - 1, item.custom_options)}
                        style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.1)", color: "#A1A1AA", width: "28px", height: "28px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "4px" }}
                        aria-label={t.cart.decreaseQty}
                      >
                        <Minus size={12} />
                      </button>
                      <span style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.8rem", color: "#FFFFFF", minWidth: "20px", textAlign: "center" }}>
                        {item.quantity}
                      </span>
                      <button
                        data-testid={`increase-${i}`}
                        onClick={() => updateQty(item._id, item.size, item.color, item.quantity + 1, item.custom_options)}
                        style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.1)", color: "#A1A1AA", width: "28px", height: "28px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "4px" }}
                        aria-label={t.cart.increaseQty}
                      >
                        <Plus size={12} />
                      </button>
                    </div>

                    <p style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 800, color: "#FFFFFF", whiteSpace: "nowrap", flexShrink: 0, minWidth: "4.5rem", textAlign: "right" }}>
                      {item.price * item.quantity} Kč
                    </p>

                    <button
                      data-testid={`remove-${i}`}
                      onClick={() => removeItem(item._id, item.size, item.color, item.custom_options)}
                      style={{ background: "transparent", border: "none", color: "#52525B", cursor: "pointer", padding: "4px", flexShrink: 0 }}
                      aria-label={t.cart.removeItem}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div>
                <div className="glass-card rounded-xl p-6 sticky top-20">
                  <p style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.6rem", color: "#FF005A", letterSpacing: "0.2em", marginBottom: "1.5rem" }}>
                    {t.cart.summary.toUpperCase()}
                  </p>
                  <div className="flex justify-between mb-2">
                    <span style={{ color: "#A1A1AA" }}>{t.cart.products}</span>
                    <span style={{ color: "#FFFFFF" }}>{total} Kč</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span style={{ color: "#A1A1AA" }}>{t.cart.shipping}</span>
                    <span style={{ color: "#FFFFFF" }}>{t.cart.shippingFrom} 99 Kč</span>
                  </div>
                  <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: "1rem", paddingTop: "1rem" }}>
                    <div className="flex justify-between mb-4">
                      <span style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, color: "#FFFFFF" }}>{t.cart.total}</span>
                      <span style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 800, color: "#FF005A", fontSize: "1.1rem" }}>{total + 99} Kč</span>
                    </div>
                    <Link
                      to="/pokladna"
                      data-testid="checkout-btn"
                      className="btn-rebel rounded-none block text-center w-full"
                    >
                      {t.cart.checkout}
                    </Link>
                    <Link
                      to="/merch"
                      data-testid="back-to-shop"
                      className="btn-outline-rebel rounded-none block text-center w-full mt-3"
                      style={{ fontSize: "0.6rem", padding: "0.6rem" }}
                    >
                      {t.cart.continueShopping}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
